import { Component, OnInit, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth.service';
import { UrlConstantsService } from 'src/app/services/url-constants.service';

const EMAIL_REGEX = /^([a-z\d\.-]+)@([a-z\d-]+)\.([a-z]{2,8})(\.[a-z]{2,8})?$/;

interface IFeature {
  icon: string;
  title: string;
  text: string;
  link: string;
  wide?: boolean;
}

interface IPlan {
  name: string;
  price: number;
  tagline: string;
  features: string[];
  featured?: boolean;
}

/** Status of one day in the mock status page preview. */
type DayStatus = 'ok' | 'down' | 'maintenance';

@Component({
  selector: 'app-landing',
  templateUrl: './landing.component.html',
  styleUrls: ['./landing.component.scss'],
  standalone: false,
})
export class LandingPageComponent implements OnInit {
  /** Mobile navigation drawer. */
  readonly menuOpen = signal(false);

  // Newsletter form. Signals, because the app does not repaint after async work on its own.
  contactEmail = '';
  readonly contactEmailError = signal('');
  readonly contactEmailSuccess = signal(false);
  readonly contactSending = signal(false);

  readonly features: IFeature[] = [
    {
      icon: 'bx-pulse',
      title: 'Public status pages',
      text: 'A branded page per project with a 90-day history per component, so users check your status before they open a ticket.',
      link: '/knowledge-base/status-pages/public-status-page',
      wide: true,
    },
    {
      icon: 'bx-timer',
      title: 'Uptime checks',
      text: 'HTTP checks from every 30 seconds up to hourly, with a 10-second timeout.',
      link: '/knowledge-base/status-pages/health-checks',
    },
    {
      icon: 'bx-wrench',
      title: 'Maintenance windows',
      text: 'Announce planned work a week ahead and keep it out of your uptime.',
      link: '/knowledge-base/status-pages/maintenance-windows',
    },
    {
      icon: 'bx-bug',
      title: 'Error tracking',
      text: 'Exceptions from Angular, Django or plain HTTP, grouped into issues. Resolved issues reopen when they regress.',
      link: '/knowledge-base/error-tracking/error-tracking',
      wide: true,
    },
    {
      icon: 'bx-git-branch',
      title: 'Request tracing',
      text: 'Every SQL query and its timing for sampled requests, linked straight from the error that happened in them.',
      link: '/knowledge-base/tracing/performance-tracing',
      wide: true,
    },
    {
      icon: 'bx-map-alt',
      title: 'Source maps',
      text: 'Minified stack traces resolved back to your TypeScript, line by line.',
      link: '/knowledge-base/error-tracking/source-maps',
    },
  ];

  readonly plans: IPlan[] = [
    {
      name: 'Basic',
      price: 0,
      tagline: 'For side projects',
      features: ['Up to 2 components', 'Uptime checks every 5 minutes', 'Your own status page', 'Up to 2 users'],
    },
    {
      name: 'Standard',
      price: 5,
      tagline: 'For professional use',
      features: ['Up to 20 components', 'Uptime checks every 30 seconds', 'Your own subdomain', 'Up to 20 users'],
      featured: true,
    },
    {
      name: 'Team',
      price: 15,
      tagline: 'For teams shipping daily',
      features: [
        'Everything in Standard',
        'Error tracking',
        'Source maps',
        'Request tracing',
        'Priority support',
      ],
    },
  ];

  /** Deterministic 60-day history for the preview: mostly green, one outage, one maintenance. */
  readonly previewDays: DayStatus[] = Array.from({ length: 60 }, (_, i) =>
    i === 17 ? 'down' : i === 41 || i === 42 ? 'maintenance' : 'ok',
  );

  readonly year = new Date().getFullYear();
  readonly apiDocsUrl: string;

  constructor(
    private authService: AuthService,
    private router: Router,
    private http: HttpClient,
    private urls: UrlConstantsService,
  ) {
    this.apiDocsUrl = urls.API_DOCS;
  }

  ngOnInit(): void {
    if (this.authService.getAccessToken() && !this.authService.isTokenExpired()) {
      this.router.navigate(['/main']);
    }
  }

  toggleMenu(): void {
    this.menuOpen.update((open) => !open);
  }

  scrollTo(id: string): void {
    this.menuOpen.set(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  submitContactEmail(): void {
    const error = this.validateEmail(this.contactEmail);
    this.contactEmailError.set(error);
    if (error || this.contactSending()) return;

    this.contactSending.set(true);
    this.http.post(this.urls.NEWSLETTER_SUBSCRIBE, { email: this.contactEmail }).subscribe({
      next: () => {
        this.contactEmail = '';
        this.contactSending.set(false);
        this.contactEmailSuccess.set(true);
      },
      error: () => {
        this.contactSending.set(false);
        this.contactEmailError.set('Something went wrong. Please try again.');
      },
    });
  }

  private validateEmail(value: string): string {
    if (!value.trim()) return 'Please enter your email address.';
    if (!EMAIL_REGEX.test(value.trim().toLowerCase())) return 'That does not look like a valid email address.';
    return '';
  }
}
